import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Bell,
  House,
  ChatCircleText,
  CalendarBlank,
  CalendarCheck,
  CaretRight,
  CreditCard,
  DotsThree,
  Folders,
  User,
  UsersThree,
  SignOut,
  Gear,
  Student,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { useOrganizationConfig } from "@/context/OrganizationConfigContext";
import useUnreadMessages from "@/lib/hooks/useUnreadMessages";
import useParentBadges from "@/lib/hooks/useParentBadges";
import { useBranding, BrandLogoChip } from "@/context/BrandingContext";
import { ROLE_LABELS, ROLES } from "@/lib/roles";
import { listChildrenForParent } from "@/lib/dailyReports";
import {
  registerServiceWorker,
  wireNotificationClickRouting,
} from "@/lib/push";
import ChildAvatar from "@/components/children/ChildAvatar";
import NotificationBell from "@/components/NotificationBell";
import ErrorBoundary from "@/components/ErrorBoundary";
import SkipToMain from "@/components/a11y/SkipToMain";
import OrganizationSwitcher from "@/components/OrganizationSwitcher";

const PARENT_TABS = [
  { to: "/parent", icon: House, label: "Today", end: true, testId: "today" },
  { to: "/parent/family", icon: UsersThree, label: "Children", testId: "children" },
  { to: "/parent/activity", icon: CalendarBlank, label: "Updates", testId: "updates", moduleKey: "daily_activity" },
  { to: "/parent/messages", icon: ChatCircleText, label: "Messages", testId: "messages", moduleKey: "messaging" },
  { kind: "more", icon: DotsThree, label: "More", testId: "more" },
];

const TEACHER_TABS = [
  { to: "/teacher", icon: House, label: "Today", end: true, testId: "today" },
  { to: "/teacher/roster", icon: User, label: "Roster", testId: "roster" },
  { to: "/teacher/attendance", icon: CalendarCheck, label: "Attend", testId: "attend", moduleKey: "attendance" },
  { to: "/teacher/messages", icon: ChatCircleText, label: "Messages", testId: "messages", moduleKey: "messaging" },
  { to: "/teacher/schedule", icon: CalendarBlank, label: "Schedule", testId: "schedule", moduleKey: "staffing" },
];

const MORE_PARENT_LINKS = [
  { to: "/parent/inbox", icon: Bell, label: "Notifications" },
  { to: "/parent/documents", icon: Folders, label: "Documents & forms", moduleKey: "documents" },
  { to: "/parent/absences", icon: CalendarCheck, label: "Absences", moduleKey: "attendance" },
  { to: "/parent/events", icon: CalendarBlank, label: "Events", moduleKey: "events" },
  { to: "/parent/conferences", icon: CalendarCheck, label: "Conferences", moduleKey: "conferences" },
  { to: "/parent/billing", icon: CreditCard, label: "Billing", moduleKey: "family_billing" },
  { to: "/parent/enrollment", icon: Student, label: "Enrollment", moduleKey: "enrollment" },
  { to: "/parent/preferences", icon: Gear, label: "Settings" },
];

export default function MobileShell() {
  const { profile, role, signOut } = useAuth();
  const { isModuleEnabled } = useOrganizationConfig();
  const unread = useUnreadMessages(profile?.id);
  // Parent-only badges (cheap to call for non-parents; the hook short-
  // circuits when there's no profileId or supabase isn't configured).
  const parentBadges = useParentBadges(role === ROLES.PARENT ? profile?.id : null);
  const branding = useBranding();
  const location = useLocation();
  const navigate = useNavigate();
  const tabs = (role === ROLES.TEACHER ? TEACHER_TABS : PARENT_TABS)
    .filter((tab) => !tab.moduleKey || isModuleEnabled(tab.moduleKey));
  const moreParentLinks = MORE_PARENT_LINKS
    .filter((item) => !item.moduleKey || isModuleEnabled(item.moduleKey));
  const [familyChildren, setFamilyChildren] = useState([]);
  const [familyLoaded, setFamilyLoaded] = useState(false);
  const [navCompact, setNavCompact] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreBadgeCount =
    (isModuleEnabled("documents") ? parentBadges.missingDocs || 0 : 0) +
    (isModuleEnabled("incidents") ? parentBadges.unacknowledgedIncidents || 0 : 0);
  const moreActive =
    role === ROLES.PARENT &&
    moreParentLinks.some(
      (item) =>
        location.pathname === item.to || location.pathname.startsWith(`${item.to}/`),
    );

  // The parent shell stays visible across every parent route, so it is the
  // right place for a lightweight family glance. This is read-only and
  // intentionally falls back to the existing shell when the lookup fails.
  useEffect(() => {
    if (role !== ROLES.PARENT || !profile?.id) {
      setFamilyChildren([]);
      setFamilyLoaded(true);
      return undefined;
    }
    let alive = true;
    setFamilyLoaded(false);
    listChildrenForParent(profile.id)
      .then((children) => {
        if (!alive) return;
        setFamilyChildren(children || []);
      })
      .catch(() => {
        if (!alive) return;
        setFamilyChildren([]);
      })
      .finally(() => {
        if (alive) setFamilyLoaded(true);
      });
    return () => {
      alive = false;
    };
  }, [role, profile?.id]);

  // A compacting dock keeps the common destinations close to the thumb while
  // scrolling, but retains full labels at rest for orientation and discovery.
  useEffect(() => {
    const onScroll = () => setNavCompact(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMoreOpen(false);
  }, [location.pathname, role]);

  useEffect(() => {
    if (!moreOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      setMoreOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [moreOpen]);

  // PWA / push: register the service worker for roles that use the
  // mobile shell (parent + teacher). Teachers spend the day on a phone
  // in the classroom and need new-message alerts just like parents.
  // Admin & CMS use the DesktopShell and skip this path.
  useEffect(() => {
    if (role !== ROLES.PARENT && role !== ROLES.TEACHER) return undefined;
    registerServiceWorker();
    const unsub = wireNotificationClickRouting(navigate);
    return unsub;
  }, [role, navigate]);

  // iOS-style swipe-back: a rightward swipe that starts at the left
  // edge of the screen navigates back (when there is history to pop).
  // Only in standalone/native contexts — a regular Safari tab already
  // has the OS edge gesture, and doubling it would go back twice.
  useEffect(() => {
    const standalone =
      window.navigator.standalone === true ||
      window.matchMedia?.("(display-mode: standalone)")?.matches ||
      Boolean(window.Capacitor);
    if (!standalone) return undefined;
    let startX = null;
    let startY = null;
    const EDGE = 28; // px from the left edge that arms the gesture
    const onTouchStart = (e) => {
      const t = e.touches?.[0];
      if (!t) return;
      startX = t.clientX <= EDGE ? t.clientX : null;
      startY = t.clientY;
    };
    const onTouchEnd = (e) => {
      if (startX === null) return;
      const t = e.changedTouches?.[0];
      if (!t) return;
      const dx = t.clientX - startX;
      const dy = Math.abs(t.clientY - startY);
      startX = null;
      // Mostly-horizontal, decisive swipe → back (if there's history).
      if (dx > 70 && dy < 60 && (window.history.state?.idx ?? 0) > 0) {
        navigate(-1);
      }
    };
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchend", onTouchEnd);
    };
  }, [navigate]);

  const handleSignOut = async () => {
    await signOut();
    navigate("/login", { replace: true });
  };

  return (
    <div
      className="min-h-screen bg-background text-neutral-900"
      data-testid="mobile-shell"
      style={branding.cssVars}
    >
      <SkipToMain targetId="main" />
      <DesktopWorkspace
        role={role}
        profile={profile}
        branding={branding}
        tabs={tabs}
        familyChildren={familyChildren}
        familyLoaded={familyLoaded}
        unread={unread}
        moreBadgeCount={moreBadgeCount}
        moreOpen={moreOpen}
        moreActive={moreActive}
        onMoreToggle={() => setMoreOpen((open) => !open)}
        onSignOut={handleSignOut}
        moreLinks={moreParentLinks}
      />
      <header
        data-testid="mobile-header"
        role="banner"
        className="sticky top-0 z-30 overflow-clip rounded-b-[28px] shadow-[0_12px_34px_-20px_rgba(20,22,26,0.55)] lg:hidden"
      >
        <div
          data-testid="mobile-header-color"
          className="pt-safe-header relative overflow-clip px-5 pb-4 text-white"
          style={{
            background: `linear-gradient(125deg, ${branding.primary} 0%, ${branding.primary} 45%, ${branding.accent} 100%)`,
          }}
        >
          <div aria-hidden className="pointer-events-none absolute -right-12 -top-16 h-48 w-48 rounded-full bg-white/10" />
          <div aria-hidden className="pointer-events-none absolute -bottom-20 left-1/3 h-44 w-44 rounded-full border-[22px] border-white/10" />
          <div className="relative flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <div className="rounded-2xl bg-white/20 p-1 ring-1 ring-inset ring-white/25">
                <BrandLogoChip size={42} testId="mobile-brand-logo" />
              </div>
              <div className="flex min-w-0 flex-col leading-tight">
                <span className="inline-flex w-fit rounded-full bg-black/20 px-2 py-1 font-mono text-[9px] font-medium uppercase tracking-[0.16em] text-white/80">
                  {ROLE_LABELS[role] || "Account"}
                </span>
                <span
                  data-testid="mobile-brand-name"
                  className="mt-1 truncate font-heading text-[17px] font-semibold tracking-tight text-white"
                >
                  {branding.displayName}
                </span>
                <span className="truncate text-[12px] text-white/75">
                  {profile?.full_name || profile?.email}
                </span>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <NotificationBell variant="mobile" />
              {(role === ROLES.PARENT || role === ROLES.TEACHER) && (
                <NavLink
                  to={role === ROLES.TEACHER ? "/teacher/preferences" : "/parent/preferences"}
                  data-testid={
                    role === ROLES.TEACHER
                      ? "mobile-teacher-preferences-link"
                      : "mobile-parent-preferences-link"
                  }
                  aria-label="Notification preferences"
                  className={({ isActive }) =>
                    `flex h-11 w-11 items-center justify-center rounded-full border border-white/25 transition-all active:scale-[0.96] ${
                      isActive
                        ? "bg-white text-primary-hover"
                        : "bg-white/20 text-white hover:bg-white/25"
                    } focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-transparent`
                  }
                >
                  <Gear size={17} weight="duotone" />
                </NavLink>
              )}
              <button
                data-testid="mobile-signout-button"
                onClick={handleSignOut}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-white/25 bg-white/20 text-white transition-all hover:bg-white/25 active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
                aria-label="Sign out"
              >
                <SignOut size={17} weight="duotone" />
              </button>
            </div>
          </div>

          <OrganizationSwitcher className="relative mt-3 max-w-xs border-white/20 bg-white/15 text-white [&_select]:text-white" />

          {role === ROLES.PARENT && (
            <div
              data-testid="mobile-family-summary"
              data-family-count={familyChildren.length}
              className="relative mt-4 flex items-center gap-3 rounded-2xl border border-white/20 bg-black/20 px-3 py-2.5 shadow-inner"
            >
              <div className="flex shrink-0 items-center pl-0.5">
                {!familyLoaded ? (
                  [0, 1, 2].map((item) => (
                    <span
                      key={item}
                      aria-hidden
                      className="-ml-1 h-8 w-8 animate-pulse rounded-full border-2 border-white/50 bg-white/25 first:ml-0"
                    />
                  ))
                ) : familyChildren.length > 0 ? (
                  <>
                    {familyChildren.slice(0, 4).map((child) => (
                      <ChildAvatar
                        key={child.id}
                        path={child.profile_photo_path}
                        name={`${child.first_name || ""} ${child.last_name || ""}`.trim()}
                        size={32}
                        testId={`mobile-family-member-${child.id}`}
                        className="-ml-1 border-2 border-white/80 first:ml-0"
                      />
                    ))}
                    {familyChildren.length > 4 && (
                      <span className="-ml-1 flex h-8 w-8 items-center justify-center rounded-full border-2 border-white/80 bg-white/20 text-[10px] font-bold text-white">
                        +{familyChildren.length - 4}
                      </span>
                    )}
                  </>
                ) : (
                  <span className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-white/60 bg-white/20 text-white">
                    <UsersThree size={16} weight="duotone" />
                  </span>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-mono text-[9px] font-medium uppercase tracking-[0.14em] text-white/70">
                  Your children
                </p>
                <p className="truncate text-sm font-semibold text-white">
                  {!familyLoaded
                    ? "Loading family…"
                    : familyChildren.length === 0
                      ? "No children added yet"
                      : familyChildren.map((child) => child.first_name).join(" · ")}
                </p>
              </div>
              <NavLink
                to="/parent/family"
                aria-label="View children"
                className="inline-flex shrink-0 items-center gap-1 rounded-full bg-white/20 px-2.5 py-1.5 text-[11px] font-semibold text-white transition hover:bg-white/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
              >
                <span className="hidden sm:inline">View children</span>
                <CaretRight size={14} weight="bold" />
              </NavLink>
            </div>
          )}
        </div>
        <div
          aria-hidden
          data-testid="mobile-brand-accent"
          className="h-1 w-full"
          style={{
            background: `linear-gradient(90deg, ${branding.accent}, ${branding.primary})`,
          }}
        />
      </header>

      <main
        id="main"
        className="px-4 pb-tabbar pt-5 sm:pt-6 lg:ml-72 lg:min-h-screen lg:px-10 lg:pb-12 lg:pt-28"
        data-testid="mobile-main"
        role="main"
        tabIndex={-1}
      >
        <div className="mx-auto w-full max-w-6xl">
          <ErrorBoundary scope={role === ROLES.TEACHER ? "teacher" : "parent"}>
            <Outlet />
          </ErrorBoundary>
        </div>
      </main>

      <nav
        data-testid="mobile-bottom-nav"
        aria-label="Primary"
        className={`fixed left-1/2 z-40 -translate-x-1/2 border border-neutral-200/80 bg-white/90 pb-safe backdrop-blur-2xl transition-[bottom,width,border-radius,padding,box-shadow] duration-300 ease-out motion-reduce:transition-none lg:hidden ${
          navCompact
            ? "bottom-3 w-[calc(100%-5rem)] max-w-[23rem] rounded-full px-1.5 shadow-pop"
            : "bottom-4 w-[calc(100%-1rem)] max-w-[30rem] rounded-[28px] px-2.5 shadow-raised"
        }`}
      >
        <ul className="mx-auto flex w-full items-center gap-1 pb-1.5 pt-2">
          {tabs.map((tab) => (
            <li key={tab.testId || tab.to || tab.kind} className="relative min-w-0 flex-1">
              {tab.kind === "more" ? (
                <MoreNavButton
                  label={tab.label}
                  Icon={tab.icon}
                  testId={tab.testId}
                  active={moreActive}
                  open={moreOpen}
                  compact={navCompact}
                  badgeCount={moreBadgeCount}
                  onClick={() => setMoreOpen((open) => !open)}
                />
              ) : (
                <NavLink
                  to={tab.to}
                  end={tab.end}
                  data-testid={`mobile-tab-${tab.testId || tab.label.toLowerCase()}`}
                  aria-label={tab.label}
                  className={({ isActive }) =>
                    `group flex min-w-0 flex-col items-center gap-1 rounded-2xl px-2 py-1.5 text-[11px] font-medium transition-all duration-300 ease-out motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
                      navCompact ? "py-1" : "py-1.5"
                    } ${isActive ? "text-primary-hover" : "text-neutral-500 hover:text-neutral-800"}`
                  }
                >
                  {({ isActive }) => (
                    <NavIconContent
                      Icon={tab.icon}
                      label={tab.label}
                      isActive={isActive}
                      compact={navCompact}
                      unread={tab.testId === "messages" ? unread : 0}
                      badgeCount={0}
                    />
                  )}
                </NavLink>
              )}
            </li>
          ))}
        </ul>
        {moreOpen && role === ROLES.PARENT && (
          <MoreMenu
            links={moreParentLinks}
            onClose={() => setMoreOpen(false)}
          />
        )}
      </nav>
    </div>
  );
}

function MoreNavButton({ label, Icon, testId, active, open, compact, badgeCount, onClick }) {
  return (
    <button
      type="button"
      data-testid={`mobile-tab-${testId}`}
      aria-label={label}
      aria-expanded={open}
      aria-haspopup="menu"
      onClick={onClick}
      className={`group flex w-full min-w-0 flex-col items-center gap-1 rounded-2xl px-2 text-[11px] font-medium transition-all duration-300 ease-out motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${compact ? "py-1" : "py-1.5"} ${active || open ? "text-primary-hover" : "text-neutral-500 hover:text-neutral-800"}`}
    >
      <NavIconContent
        Icon={Icon}
        label={label}
        isActive={active || open}
        compact={compact}
        unread={0}
        badgeCount={badgeCount}
      />
    </button>
  );
}

function NavIconContent({ Icon, label, isActive, compact, unread, badgeCount }) {
  return (
    <>
      <span
        className={`relative flex items-center justify-center rounded-xl transition-all duration-300 ease-out motion-reduce:transition-none ${
          compact ? "h-8 w-8" : "h-9 w-9"
        } ${
          isActive
            ? "bg-primary text-white shadow-honey-glow"
            : "bg-neutral-100/80 text-neutral-500 group-hover:bg-neutral-100 group-hover:text-neutral-800"
        }`}
      >
        <Icon size={compact ? 18 : 19} weight={isActive ? "fill" : "duotone"} aria-hidden />
        {unread > 0 && (
          <span
            data-testid="mobile-nav-unread-badge"
            data-count={unread}
            className="absolute -right-1.5 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-danger px-1 text-[9px] font-bold leading-none text-white"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
        {badgeCount > 0 && (
          <span
            data-testid="mobile-nav-more-badge"
            data-count={badgeCount}
            className="absolute -right-1.5 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-danger px-1 text-[9px] font-bold leading-none text-white"
          >
            {badgeCount > 99 ? "99+" : badgeCount}
          </span>
        )}
      </span>
      <span className={`overflow-hidden whitespace-nowrap transition-all duration-300 ease-out motion-reduce:transition-none ${compact ? "sr-only" : "max-h-5 opacity-100"}`}>
        {label}
      </span>
    </>
  );
}

function MoreMenu({ links, onClose }) {
  return (
    <div
      data-testid="mobile-more-menu"
      role="menu"
      aria-label="More parent actions"
      className="absolute bottom-full left-1/2 mb-2 w-[min(22rem,calc(100vw-1.5rem))] -translate-x-1/2 rounded-3xl border border-neutral-200 bg-white/95 p-2 shadow-xl backdrop-blur-2xl"
    >
      <p className="px-3 pb-2 pt-2 text-[10px] font-bold uppercase tracking-[0.16em] text-neutral-500">
        For your family
      </p>
      <div className="grid grid-cols-2 gap-1">
        {links.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            role="menuitem"
            onClick={onClose}
            data-testid={`mobile-more-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className={({ isActive }) =>
              `flex min-h-12 items-center gap-2 rounded-2xl px-3 py-2 text-xs font-semibold transition ${isActive ? "bg-primary/10 text-primary-hover" : "text-neutral-700 hover:bg-neutral-100"}`
            }
          >
            <Icon size={17} weight="duotone" className="shrink-0" />
            <span className="truncate">{label}</span>
          </NavLink>
        ))}
      </div>
    </div>
  );
}

function DesktopWorkspace({
  role,
  profile,
  branding,
  tabs,
  familyChildren,
  familyLoaded,
  unread,
  moreBadgeCount,
  moreOpen,
  moreActive,
  onMoreToggle,
  onSignOut,
  moreLinks,
}) {
  const isParent = role === ROLES.PARENT;

  return (
    <>
      <aside
        data-testid={isParent ? "desktop-parent-sidebar" : "desktop-mobile-shell-sidebar"}
        className="fixed inset-y-0 left-0 z-40 hidden w-72 flex-col border-r border-neutral-200 bg-white px-5 py-6 lg:flex"
      >
        <div
          className="relative overflow-hidden rounded-3xl p-4 text-white shadow-card"
          style={{
            background: `linear-gradient(135deg, ${branding.primary} 0%, ${branding.primary} 55%, ${branding.accent} 100%)`,
          }}
        >
          <div aria-hidden className="pointer-events-none absolute -right-10 -top-12 h-32 w-32 rounded-full bg-white/10" />
          <div className="relative flex items-center gap-3">
            <div className="rounded-2xl bg-white/20 p-1 ring-1 ring-inset ring-white/25">
              <BrandLogoChip size={40} testId="desktop-mobile-shell-logo" />
            </div>
            <div className="min-w-0">
              <p className="font-mono text-[9px] font-medium uppercase tracking-[0.16em] text-white/75">
                {ROLE_LABELS[role] || "Workspace"}
              </p>
              <p className="truncate font-heading text-base font-semibold tracking-tight text-white">
                {branding.displayName}
              </p>
            </div>
          </div>
          {isParent && (
            <div className="relative mt-4 flex items-center gap-2 rounded-2xl bg-black/15 px-2.5 py-2">
              <div className="flex shrink-0 items-center">
                {familyLoaded && familyChildren.length > 0 ? (
                  familyChildren.slice(0, 3).map((child) => (
                    <ChildAvatar
                      key={child.id}
                      path={child.profile_photo_path}
                      name={`${child.first_name || ""} ${child.last_name || ""}`.trim()}
                      size={25}
                      className="-ml-1 border-2 border-white/80 first:ml-0"
                    />
                  ))
                ) : (
                  <UsersThree size={18} weight="duotone" />
                )}
              </div>
              <div className="min-w-0">
                <p className="font-mono text-[9px] uppercase tracking-[0.14em] text-white/65">Your children</p>
                <p className="truncate text-xs font-semibold text-white">
                  {!familyLoaded ? "Loading…" : familyChildren.length ? familyChildren.map((child) => child.first_name).join(" · ") : "Add your first child"}
                </p>
              </div>
            </div>
          )}
        </div>

        <nav aria-label="Desktop primary" className="mt-7 flex-1 overflow-y-auto pr-1">
          <p className="px-3 pb-2 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-neutral-400">
            {isParent ? "Your family" : "Your workspace"}
          </p>
          <ul className="space-y-1">
            {tabs.filter((tab) => tab.kind !== "more").map((tab) => (
              <li key={tab.testId || tab.to}>
                <DesktopNavLink tab={tab} unread={tab.testId === "messages" ? unread : 0} />
              </li>
            ))}
          </ul>

          {isParent && (
            <div className="mt-6 border-t border-neutral-100 pt-5">
              <button
                type="button"
                data-testid="desktop-parent-more-toggle"
                aria-expanded={moreOpen || moreActive}
                onClick={onMoreToggle}
                className={`flex w-full items-center justify-between rounded-2xl px-3 py-2.5 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${moreOpen || moreActive ? "bg-neutral-100 text-neutral-900" : "text-neutral-600 hover:bg-neutral-50"}`}
              >
                <span className="flex items-center gap-3">
                  <DotsThree size={20} weight="bold" />
                  More for your family
                </span>
                <span className="flex items-center gap-2">
                  {moreBadgeCount > 0 && (
                    <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-danger px-1 text-[10px] font-bold text-white">
                      {moreBadgeCount > 99 ? "99+" : moreBadgeCount}
                    </span>
                  )}
                  <CaretRight size={16} className={`transition-transform ${moreOpen || moreActive ? "rotate-90" : ""}`} />
                </span>
              </button>
              {(moreOpen || moreActive) && (
                <ul className="mt-1 space-y-1 pl-2">
                  {moreLinks.map((item) => (
                    <li key={item.to}>
                      <DesktopNavLink tab={item} compact />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </nav>

        <div className="border-t border-neutral-200 pt-4">
          <div className="mb-3 flex items-center gap-3 px-1">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-neutral-100 text-sm font-semibold text-neutral-700">
              {(profile?.full_name || profile?.email || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-neutral-900">{profile?.full_name || "—"}</p>
              <p className="truncate text-xs text-neutral-500">{profile?.email}</p>
            </div>
          </div>
          <OrganizationSwitcher className="mt-3" />
          <button
            type="button"
            data-testid="desktop-mobile-shell-signout-button"
            onClick={onSignOut}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-neutral-100 px-4 py-2 text-sm font-medium text-neutral-800 transition hover:bg-neutral-200 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            <SignOut size={16} weight="duotone" />
            Sign out
          </button>
        </div>
      </aside>

      <header
        data-testid="desktop-mobile-shell-header"
        className="fixed left-72 right-0 top-0 z-30 hidden h-24 border-b border-neutral-200/80 bg-background/90 px-10 backdrop-blur-xl lg:flex lg:items-center"
      >
        <div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-6">
          <div className="min-w-0">
            <p className="overline text-primary-hover">{isParent ? "Parent workspace" : "Teacher workspace"}</p>
            <h1 className="mt-1 truncate font-heading text-2xl font-bold tracking-tight text-neutral-900">
              {isParent ? "Your children, at a glance" : "Your classroom, at a glance"}
            </h1>
            <p className="mt-1 truncate text-sm text-neutral-500">
              {isParent
                ? familyLoaded && familyChildren.length > 0
                  ? familyChildren.map((child) => child.first_name).join(" · ")
                  : "Everything your family needs in one place"
                : profile?.full_name || "Stay close to today’s care"}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <NotificationBell variant="desktop" />
            <NavLink
              to={isParent ? "/parent/preferences" : "/teacher/preferences"}
              aria-label="Settings"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-neutral-200 bg-white text-neutral-600 transition hover:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            >
              <Gear size={17} weight="duotone" />
            </NavLink>
          </div>
        </div>
      </header>
    </>
  );
}

function DesktopNavLink({ tab, unread = 0, compact = false }) {
  const Icon = tab.icon;
  return (
    <NavLink
      to={tab.to}
      end={tab.end}
      data-testid={`desktop-nav-${tab.testId || tab.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className={({ isActive }) =>
        `group flex items-center gap-3 rounded-2xl ${compact ? "px-3 py-2" : "px-3 py-3"} text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${isActive ? "bg-primary/10 text-primary-hover" : "text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900"}`
      }
    >
      {({ isActive }) => (
        <>
          <span className={`relative flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${isActive ? "bg-primary text-white shadow-honey-glow" : "bg-neutral-100 text-neutral-500 group-hover:text-neutral-800"}`}>
            <Icon size={18} weight={isActive ? "fill" : "duotone"} />
            {unread > 0 && (
              <span className="absolute -right-1.5 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[9px] font-bold text-white">
                {unread > 99 ? "99+" : unread}
              </span>
            )}
          </span>
          <span className="min-w-0 truncate">{tab.label}</span>
        </>
      )}
    </NavLink>
  );
}
